import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    Typography,
    Alert,
    CircularProgress
} from '@mui/material';
import EditQuestionModal from './EditQuestionModal';

// Icons
import AddIcon from '@mui/icons-material/Add';
import ApplicationSettings from './ApplicationSettings';
import SectionPanel from './SectionPanel';
import ApplicationHelpPanel from './ApplicationHelpPanel';
import { ApplicationDetails, Section, CampaignMode, ApplicationQuestion } from '../../../../types/Applications';
import ApplicationService from '../../../../services/ApplicationService';

// Questions Component Props
interface QuestionsProps {
    campaignId: string;
    organizationId: string;
}

// Questions Panel Component
const QuestionsPanel: React.FC<QuestionsProps> = ({ campaignId, organizationId }): JSX.Element => {
    // Application details state
    const [applicationDetails, setApplicationDetails] = useState<ApplicationDetails>({
        id: '',
        campaign_id: campaignId,
        organization_id: organizationId,
        include_availability: true,
        include_location: true,
        campaign_mode: CampaignMode.Hybrid,
        sections: [],
        questions: []
    });

    // Loading and error states
    const [loading, setLoading] = useState<boolean>(true);
    const [updating, setUpdating] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [updateError, setUpdateError] = useState<string | null>(null);
    
    // Edit question modal state
    const [showEditModal, setShowEditModal] = useState<boolean>(false);
    const [currentQuestion, setCurrentQuestion] = useState<ApplicationQuestion | null>(null);
    const [currentSectionId, setCurrentSectionId] = useState<string>('');

    // Update application details in the backend
    const updateApplicationDetails = async (updatedDetails: ApplicationDetails) => {
        setUpdating(true);
        setUpdateError(null);

        try {
            const applicationService = new ApplicationService();
            const result = await applicationService.updateApplication(
                organizationId,
                campaignId,
                updatedDetails
            );
            console.log('Application details updated successfully:', result);
        } catch (err) {
            console.error('Error updating application details:', err);
            setUpdateError('Failed to save changes. Your changes are visible but not saved to the server.');
        } finally {
            setUpdating(false);
        }
    };

    // Handler for availability checkbox changes
    const handleAvailabilityChange = (checked: boolean) => {
        const updatedDetails = {
            ...applicationDetails,
            include_availability: checked
        };

        setApplicationDetails(updatedDetails);
        updateApplicationDetails(updatedDetails);
    };

    // Handler for location checkbox changes
    const handleLocationChange = (checked: boolean) => {
        const updatedDetails = {
            ...applicationDetails,
            include_location: checked
        };

        setApplicationDetails(updatedDetails);
        updateApplicationDetails(updatedDetails);
    };

    // Handler for campaign mode changes
    const handleCampaignModeChange = (mode: CampaignMode) => {
        const updatedDetails = {
            ...applicationDetails,
            campaign_mode: mode
        };

        setApplicationDetails(updatedDetails);
        updateApplicationDetails(updatedDetails);
        console.log('Campaign mode changed to:', mode);
    };

    // Handler for section title updates
    const handleSectionTitleUpdate = (sectionId: string, newTitle: string) => {
        // Update the section title in state
        const updatedSections = applicationDetails.sections.map(section =>
            section.id === sectionId ? { ...section, title: newTitle } : section
        );

        const updatedDetails = {
            ...applicationDetails,
            sections: updatedSections
        };

        setApplicationDetails(updatedDetails);
        updateApplicationDetails(updatedDetails);
    };

    // Question edit handler
    const handleEditQuestion = (questionId: string) => {
        const question = applicationDetails.questions.find(q => q.id === questionId);
        if (question) {
            setCurrentQuestion(question);
            setCurrentSectionId(question.section_id);
            setShowEditModal(true);
        }
    };
    
    const handleAddQuestion = (sectionId: string) => {
        // Create a new empty question for the specified section
        const newQuestion: ApplicationQuestion = {
            id: generateUUID(),
            question: '',
            type: 'short_answer',
            required: false,
            section_id: sectionId,
            for_role: 'both',
            matching_importance: 'none'
        };
        
        setCurrentQuestion(newQuestion);
        setCurrentSectionId(sectionId);
        setShowEditModal(true);
    };
    
    const handleQuestionSave = (question: ApplicationQuestion) => {
        let updatedQuestions: ApplicationQuestion[];
        
        // Check if we're updating an existing question or adding a new one
        const existingIndex = applicationDetails.questions.findIndex(q => q.id === question.id);
        
        if (existingIndex >= 0) {
            // Update existing question
            updatedQuestions = [...applicationDetails.questions];
            updatedQuestions[existingIndex] = question;
        } else {
            // Add new question
            updatedQuestions = [...applicationDetails.questions, question];
        }
        
        // Update application details
        const updatedDetails = {
            ...applicationDetails,
            questions: updatedQuestions
        };
        
        // Update state and persist to backend
        setApplicationDetails(updatedDetails);
        updateApplicationDetails(updatedDetails);
        setShowEditModal(false);
    };
    
    const handleModalClose = () => {
        setShowEditModal(false);
        setCurrentQuestion(null);
    };

    // Question delete handler
    const handleDeleteQuestion = (questionId: string) => {
        // Check if the question exists
        if (applicationDetails.questions) {
            // Filter out the deleted question
            const updatedQuestions = applicationDetails.questions.filter(
                question => question.id !== questionId
            );

            // Update application details with the filtered questions
            const updatedDetails = {
                ...applicationDetails,
                questions: updatedQuestions
            };

            // Update state and persist to backend
            setApplicationDetails(updatedDetails);
            updateApplicationDetails(updatedDetails);
        }
    };

    // Generate a UUID v4 (GUID)
    const generateUUID = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    };
    
    // Handler for adding a new section
    const handleAddSection = () => {
        // Generate a GUID for the new section
        const newSectionId = generateUUID();
        
        // Create a new section with title "New Section"
        const newSection: Section = {
            id: newSectionId,
            title: "New Section"
        };
        
        // Add the new section to the existing sections
        const updatedSections = [...applicationDetails.sections, newSection];
        
        // Update application details with the new section
        const updatedDetails = {
            ...applicationDetails,
            sections: updatedSections
        };
        
        // Update state and persist to backend
        setApplicationDetails(updatedDetails);
        updateApplicationDetails(updatedDetails);
    };

    // Fetch application data when component mounts
    useEffect(() => {
        console.log('Fetching application data for campaign:', campaignId);
        fetchApplicationData();
    }, [campaignId, organizationId]); // Only re-run when IDs change

    const fetchApplicationData = async () => {
        setLoading(true);
        setError(null);

        try {
            const applicationService = new ApplicationService();
            const applicationData = await applicationService.getApplication(organizationId, campaignId);

            console.log('Raw application data loaded:', applicationData);

            // Ensure data has the expected structure
            const updatedDetails = {
                id: applicationData.id || '',
                campaign_id: applicationData.campaign_id || campaignId,
                organization_id: applicationData.organization_id || organizationId,
                include_availability: typeof applicationData.include_availability === 'boolean' ?
                    applicationData.include_availability : true,
                include_location: typeof applicationData.include_location === 'boolean' ?
                    applicationData.include_location : true,
                campaign_mode: applicationData.campaign_mode || '',
                sections: applicationData.sections || [],
                questions: applicationData.questions || []
            };

            console.log('Processed application details:', updatedDetails);
            setApplicationDetails(updatedDetails);
        } catch (err) {
            console.error('Error fetching application data:', err);
            setError('Failed to load application data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            {/* Loading indicator */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {/* Error message */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                    <Button
                        size="small"
                        sx={{ ml: 2 }}
                        onClick={fetchApplicationData}
                    >
                        Retry
                    </Button>
                </Alert>
            )}

            {/* Update error message */}
            {updateError && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    {updateError}
                    <Button
                        size="small"
                        sx={{ ml: 2 }}
                        onClick={() => updateApplicationDetails(applicationDetails)}
                        disabled={updating}
                    >
                        Retry Save
                    </Button>
                </Alert>
            )}

            {/* Update status indicator */}
            {updating && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 2 }}>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                        Saving changes...
                    </Typography>
                </Box>
            )}

            {/* Main content - only show when not loading */}
            {!loading && !error && (
                <>
                    {/* Help panel - collapsed by default */}
                    <ApplicationHelpPanel initialShowHelp={false} />

                    {/* Application settings */}
                    <ApplicationSettings
                        applicationDetails={applicationDetails}
                        onAvailabilityChange={handleAvailabilityChange}
                        onLocationChange={handleLocationChange}
                        onCampaignModeChange={handleCampaignModeChange}
                    />

                    {/* Render each section */}
                    {applicationDetails.sections && applicationDetails.sections.length > 0 ? (
                        applicationDetails.sections.map(section => {
                            // Filter questions that belong to this section

                            // Filter using section_id (snake_case from API) instead of sectionId (camelCase in our interfaces)
                            const sectionQuestions = applicationDetails.questions
                                ? applicationDetails.questions.filter(q => q.section_id === section.id)
                                : [];
                            console.log('SectionPanel questions:', {
                                sectionId: section.id,
                                allQuestions: applicationDetails.questions,
                                questions: sectionQuestions
                            });
                            return (
                                <SectionPanel
                                    key={section.id}
                                    section={section}
                                    questions={sectionQuestions}
                                    onSectionTitleUpdate={handleSectionTitleUpdate}
                                    onEditQuestion={handleEditQuestion}
                                    onDeleteQuestion={handleDeleteQuestion}
                                    onAddQuestion={handleAddQuestion}
                                />
                            );
                        })
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 4, bgcolor: 'background.paper', borderRadius: 2, mb: 2 }}>
                            <Typography variant="body1" color="text.secondary">
                                No sections added yet. Create sections to organize your application questions.
                            </Typography>
                        </Box>
                    )}

                    {/* Add Section Button */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 4 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<AddIcon />}
                            sx={{ borderRadius: 28, px: 3 }}
                            onClick={handleAddSection}
                            disabled={updating}
                        >
                            Create New Section
                        </Button>
                    </Box>
                    
                    {/* Question Edit Modal */}
                    {showEditModal && currentQuestion && (
                        <EditQuestionModal
                            open={showEditModal}
                            question={currentQuestion}
                            questions={applicationDetails.questions}
                            onClose={handleModalClose}
                            onSave={handleQuestionSave}
                        />
                    )}
                </>
            )}
        </Box>
    );
};

export default QuestionsPanel;